/**
 * ReportSummaryCard Component
 *
 * Metric display card for report summaries.
 * Optimized for both screen and print display.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface ReportSummaryCardProps {
  label: string;
  value: string | number;
  unit?: string;
  icon?: LucideIcon;
  description?: string;
  className?: string;
}

export function ReportSummaryCard({
  label,
  value,
  unit,
  icon: Icon,
  description,
  className,
}: ReportSummaryCardProps) {
  // Format large numbers with commas
  const formattedValue = typeof value === 'number'
    ? value.toLocaleString(undefined, { maximumFractionDigits: 1 })
    : value;

  return (
    <div
      className={cn(
        'report-summary-card',
        'bg-background rounded-lg border p-4 shadow-sm',
        'print:border print:border-gray-300 print:shadow-none print:p-3',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 print:text-[8pt]">
            {label}
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-foreground print:text-lg">
              {formattedValue}
            </span>
            {unit && (
              <span className="text-sm text-muted-foreground print:text-xs">
                {unit}
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-1 print:hidden">
              {description}
            </p>
          )}
        </div>
        {Icon && (
          <div className="p-2 bg-primary/10 rounded-md print:hidden">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ReportSummaryGrid Component
 *
 * Grid container for multiple summary cards.
 */
interface ReportSummaryGridProps {
  children: React.ReactNode;
  className?: string;
}

export function ReportSummaryGrid({ children, className }: ReportSummaryGridProps) {
  return (
    <div
      className={cn(
        'report-summary-grid',
        'grid grid-cols-2 md:grid-cols-3 gap-4',
        'print:grid-cols-3 print:gap-2',
        className
      )}
    >
      {children}
    </div>
  );
}
